import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { isArray } from "lodash";
import { FC, SyntheticEvent, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  bulkMultiselectActionsSelectItems,
  checkCleanupNestedValidation,
  dropdownsIsFetchingAtom,
  excludeNotArrayValue,
  getBulkFormFieldName,
  getBulkMultiselectValueFormValidation,
  ICustomField,
  Log,
  onlyNumericArrayValues,
  requiredRuleStringField,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { BulkActionFieldStackStyled } from "./bulk-action-field-stack.styled";
import { DropdownTemplate } from "./dropdown.template";
import { useDropdownTemplate } from "./dropdowns";
import { useClearBulkMultiselectOnAction } from "./use-clear-bulk-multiselect-on-action";
import { useIsBulkControlSelected } from "./use-is-bulk-control-selected";

export const BulkTagsTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  formAttributes,
  e2eData = "tags-template",
}) => {
  const { t } = useTranslation();
  const isBulkControlSelected = useIsBulkControlSelected(name);
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext();
  const bulkAction = watch(getBulkFormFieldName(name));
  useClearBulkMultiselectOnAction(name);

  const {
    requiredComputed,
    isDisabled,
    dropdownOptions,
    handleInsertTagItem,
    getOptionLabel,
  } = useDropdownTemplate(
    "tags",
    name,
    formAttributes,
    getBulkMultiselectValueFormValidation(isBulkControlSelected, bulkAction),
  );

  const { disableSorting } = formAttributes || {};
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });

  const loadingItems = useRecoilValue(dropdownsIsFetchingAtom);
  const renderValue: number[] = getValues(name);
  useEffect(() => {
    if (renderValue && !isArray(renderValue)) {
      /* guard for invalid value coming from backend*/
      Log.warn(
        t("errors.multiSelectBoundTemplateInitialValueNotArray"),
        renderValue,
      );
      setValue(name, []);
    }
  }, [name, renderValue, setValue, t]);

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: number[],
  ): void => {
    Log.debug("Tags template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const {
    field: { value: formValue, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const sortedValue = useMemo(() => {
    const currentSelection = onlyNumericArrayValues(
      excludeNotArrayValue<string | number>(formValue),
    );
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  return (
    <FormControl
      fullWidth
      data-e2e-template="tags-template"
      data-e2e-field-name={name}
      required={requiredComputed}
      error={!!error}
    >
      <BulkActionFieldStackStyled>
        <DropdownTemplate
          name={getBulkFormFieldName(name)}
          formAttributes={{
            selectItems: bulkMultiselectActionsSelectItems,
          }}
          formValidation={
            isBulkControlSelected ? requiredRuleStringField : undefined
          }
          labelTranslated={t("general.multiselectBulkAction")}
        />
        <Autocomplete
          {...restField}
          data-e2e={e2eData}
          data-e2e-id={`${name}_${id || ""}`}
          disabled={isDisabled || loadingItems}
          multiple
          freeSolo
          options={dropdownOptions || []}
          value={value}
          onChange={async (event, values) => {
            checkCleanupNestedValidation(name, errors);

            const lastValue = values[values.length - 1] as string;
            const isNewValue =
              lastValue && !dropdownOptions?.includes(lastValue);

            if (isNewValue) {
              Log.debug("New value entered:", lastValue);
              const saveTagResult = await handleInsertTagItem(lastValue);

              if (!saveTagResult.success) {
                warnAndToastSimpleResult(saveTagResult, t);
              }
            } else {
              onChangeDropdown(event, values as number[]);
            }
          }}
          isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
          getOptionLabel={getOptionLabel}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={formLabelTranslated}
                error={!!error}
                required={requiredComputed}
                disabled={isDisabled || loadingItems}
                data-e2e="tags-template-input"
              />
            );
          }}
        />
      </BulkActionFieldStackStyled>
      {error && (
        <FormHelperText data-e2e="tags-template-error">
          {error.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};
